import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker, Line } from 'react-simple-maps';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Country centroids [lng, lat] — covers GTM_Accounts + common CRM countries
const COUNTRY_COORDS = {
    'USA': [-98, 39], 'United States': [-98, 39], 'US': [-98, 39],
    'UK': [-2, 54], 'United Kingdom': [-2, 54], 'England': [-2, 54],
    'Singapore': [103.8, 1.35],
    'Germany': [10.4, 51.2],
    'Japan': [138, 36],
    'India': [78.9, 20.6],
    'Australia': [133.7, -25.3],
    'UAE': [54, 24], 'United Arab Emirates': [54, 24],
    'Philippines': [121.8, 12.9],
    'Indonesia': [113.9, -0.8],
    'France': [2.2, 46.2],
    'Canada': [-106, 56],
    'Switzerland': [8.2, 46.8],
    'Netherlands': [5.3, 52.1],
    'Hong Kong': [114.2, 22.3],
    'China': [104.2, 35.9],
    'Thailand': [100.9, 15.9],
    'Malaysia': [101.9, 4.2],
    'South Korea': [127.8, 35.9],
    'Spain': [-3.7, 40.5],
    'Italy': [12.6, 41.9],
    'Brazil': [-51.9, -14.2],
    'Mexico': [-102.6, 23.6],
    'Saudi Arabia': [45, 23.9],
    'Qatar': [51.2, 25.4],
};

function normCountry(c) {
    if (!c) return null;
    const t = c.trim();
    return COUNTRY_COORDS[t] ? t : null;
}

// Map free-text Territory → country
const TERRITORY_MAP = {
    'apac': 'Singapore', 'asia': 'Singapore', 'asia pacific': 'Singapore',
    'emea': 'UK', 'europe': 'UK', 'uk': 'UK', 'england': 'UK',
    'nam': 'USA', 'north america': 'USA', 'americas': 'USA', 'us': 'USA', 'usa': 'USA',
    'latam': 'Brazil',
    'mena': 'UAE', 'middle east': 'UAE',
    'india': 'India', 'singapore': 'Singapore', 'japan': 'Japan',
    'germany': 'Germany', 'australia': 'Australia', 'philippines': 'Philippines',
    'indonesia': 'Indonesia', 'thailand': 'Thailand', 'malaysia': 'Malaysia',
    'uae': 'UAE', 'france': 'France', 'canada': 'Canada',
};
function territoryToCountry(t) {
    if (!t) return 'Singapore';
    const key = t.trim().toLowerCase();
    if (TERRITORY_MAP[key]) return TERRITORY_MAP[key];
    for (const k in TERRITORY_MAP) if (key.includes(k)) return TERRITORY_MAP[k];
    if (COUNTRY_COORDS[t.trim()]) return t.trim();
    return 'Singapore';
}

// Jitter a coordinate so multiple markers at same country don't overlap
function jitter(coords, i, total) {
    if (total <= 1) return coords;
    const angle = (i / total) * 2 * Math.PI;
    const r = 1.5 + Math.min(3, total * 0.2);
    return [coords[0] + Math.cos(angle) * r, coords[1] + Math.sin(angle) * r];
}

// Extract "root" company name for fuzzy multinational matching
// "HSBC London" → "hsbc", "Reliance Jio" → "reliance"
function rootName(name) {
    if (!name) return '';
    return name.toLowerCase()
        .replace(/\b(ltd|limited|inc|corp|corporation|plc|pte|group|bank|holdings|co|llc|sa|ag|gmbh|nv|bv)\b/gi, '')
        .trim()
        .split(/[\s,.-]+/)[0] || '';
}

const NODE_COLORS = {
    deal: '#10b981', internal: '#ec4899', external: '#06b6d4',
    activity: '#f97316', partnership: '#8b5cf6', investor: '#f59e0b',
    gtm: '#0d9488', usecase: '#2563eb',
};

export default function WorldRelationshipView() {
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [deals, setDeals] = useState([]);
    const [externals, setExternals] = useState([]);
    const [internal, setInternal] = useState([]);
    const [activities, setActivities] = useState([]);
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [focusCountry, setFocusCountry] = useState(null); // country name
    const [focusCompany, setFocusCompany] = useState(null); // company name
    const [connections, setConnections] = useState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [center, setCenter] = useState([10, 20]);
    const [hoverCountry, setHoverCountry] = useState(null);
    const [hoverArc, setHoverArc] = useState(null);
    const [cinematic, setCinematic] = useState(true);
    const searchTimer = useRef();

    useEffect(() => {
        (async () => {
            try {
                const [gtmR, dealR, extR, intR, actR] = await Promise.all([
                    fetch(`${API_URL}/gtm-accounts`).then(r => r.ok ? r.json() : []).catch(() => []),
                    fetch(`${API_URL}/deals`).then(r => r.ok ? r.json() : []).catch(() => []),
                    fetch(`${API_URL}/external-contacts`).then(r => r.ok ? r.json() : []).catch(() => []),
                    fetch(`${API_URL}/internal`).then(r => r.ok ? r.json() : []).catch(() => []),
                    fetch(`${API_URL}/activities`).then(r => r.ok ? r.json() : []).catch(() => []),
                ]);
                setAccounts(Array.isArray(gtmR) ? gtmR : []);
                setDeals(Array.isArray(dealR) ? dealR : []);
                setExternals(Array.isArray(extR) ? extR : []);
                setInternal(Array.isArray(intR) ? intR : []);
                setActivities(Array.isArray(actR) ? actR : []);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Aggregate counts per country
    const countryData = useMemo(() => {
        const map = {};
        accounts.forEach(a => {
            const c = normCountry(a.Country || a.country);
            if (!c) return;
            if (!map[c]) map[c] = { country: c, accounts: [], deals: [], contacts: [] };
            map[c].accounts.push(a);
        });
        deals.forEach(d => {
            const companyName = (d.Company || d.company || '').trim();
            if (!companyName) return;
            // match deal to account country by company name
            const match = accounts.find(a => (a.Name || a.name || '').toLowerCase() === companyName.toLowerCase());
            const c = match ? normCountry(match.Country || match.country) : null;
            if (!c) return;
            map[c].deals.push(d);
        });
        externals.forEach(e => {
            const company = (e.Company || e.company || '').trim();
            if (!company) return;
            const match = accounts.find(a => (a.Name || a.name || '').toLowerCase() === company.toLowerCase());
            const c = match ? normCountry(match.Country || match.country) : null;
            if (!c) return;
            map[c].contacts.push(e);
        });
        return Object.values(map);
    }, [accounts, deals, externals]);

    // Search suggestions
    useEffect(() => {
        if (!query || query.length < 2) { setSuggestions([]); return; }
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(async () => {
            try {
                const r = await fetch(`${API_URL}/graph-search?q=${encodeURIComponent(query)}`);
                const data = await r.json();
                setSuggestions(Array.isArray(data) ? data.slice(0, 10) : []);
            } catch { setSuggestions([]); }
        }, 250);
    }, [query]);

    const zoomToCountry = (countryName) => {
        const coords = COUNTRY_COORDS[countryName];
        if (!coords) return;
        setFocusCountry(countryName);
        // no longer zoom map — constellation overlay handles it
    };

    const backToWorld = () => {
        setFocusCountry(null);
        setFocusCompany(null);
        setConnections([]);
        setSelectedNode(null);
        setCenter([10, 20]);
        setZoom(1);
    };

    const pickSuggestion = async (s) => {
        setQuery('');
        setSuggestions([]);
        let country = null;
        if (s.nodeType === 'gtm') {
            country = normCountry(s.Country);
        } else if (s.nodeType === 'deal' || s.nodeType === 'external') {
            const company = s.Company;
            const match = accounts.find(a => (a.Name || '').toLowerCase() === (company || '').toLowerCase());
            country = match ? normCountry(match.Country) : null;
        }
        if (country) zoomToCountry(country);
        setFocusCompany(s.name);
        setSelectedNode(s);
        await loadConnections(s.nodeType, s.Id);
    };

    const loadConnections = async (nodeType, nodeId) => {
        try {
            const r = await fetch(`${API_URL}/graph-search?expandId=${nodeId}&expandType=${nodeType}`);
            const data = await r.json();
            setConnections(Array.isArray(data) ? data : []);
        } catch { setConnections([]); }
    };

    const focusCountryData = focusCountry ? countryData.find(c => c.country === focusCountry) : null;

    // Bundled global arcs — compute once: for each pair of countries, group multinationals sharing rootName
    const globalArcs = useMemo(() => {
        const groups = {};
        const byRoot = {};
        accounts.forEach(a => {
            const root = rootName(a.Name || a.name);
            const country = normCountry(a.Country || a.country);
            if (!root || !country || root.length < 3) return;
            if (!byRoot[root]) byRoot[root] = [];
            byRoot[root].push({ country, name: a.Name || a.name });
        });
        Object.entries(byRoot).forEach(([root, accts]) => {
            const countries = [...new Set(accts.map(a => a.country))];
            if (countries.length < 2) return;
            for (let i = 0; i < countries.length; i++) {
                for (let j = i + 1; j < countries.length; j++) {
                    const [a, b] = [countries[i], countries[j]].sort();
                    const key = `${a}|${b}`;
                    if (!groups[key]) groups[key] = { from: a, to: b, roots: new Set(), companies: [] };
                    groups[key].roots.add(root);
                    groups[key].companies.push(...accts.filter(x => x.country === a || x.country === b).map(x => x.name));
                }
            }
        });
        return Object.values(groups).map(g => ({
            from: g.from, to: g.to,
            fromCoords: COUNTRY_COORDS[g.from], toCoords: COUNTRY_COORDS[g.to],
            rootCount: g.roots.size,
            rootLabel: [...g.roots].slice(0, 3).map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ') + (g.roots.size > 3 ? `, +${g.roots.size - 3}` : ''),
            companies: [...new Set(g.companies)],
        })).filter(a => a.fromCoords && a.toCoords);
    }, [accounts]);

    // Compute arcs: siblings of focused company in OTHER countries (multinational footprint)
    const focusArcs = useMemo(() => {
        if (!focusCompany || !focusCountry) return [];
        const root = rootName(focusCompany);
        if (!root || root.length < 3) return [];
        const fromCoords = COUNTRY_COORDS[focusCountry];
        if (!fromCoords) return [];
        // Group siblings by destination country
        const byCountry = {};
        accounts.forEach(a => {
            const name = a.Name || a.name || '';
            if (!name || name.toLowerCase() === focusCompany.toLowerCase()) return;
            if (rootName(name) !== root) return;
            const c = normCountry(a.Country || a.country);
            if (!c || c === focusCountry) return;
            if (!byCountry[c]) byCountry[c] = [];
            byCountry[c].push(a);
        });
        return Object.entries(byCountry).map(([country, accts]) => ({
            from: fromCoords,
            to: COUNTRY_COORDS[country],
            toCountry: country,
            count: accts.length,
            // max deal value among siblings in this country
            maxVal: accts.reduce((m, a) => {
                const d = deals.find(d => (d.Company || '').toLowerCase() === (a.Name || '').toLowerCase());
                return Math.max(m, Number(d?.Value || 0));
            }, 0),
        }));
    }, [focusCompany, focusCountry, accounts, deals]);

    return (
        <div style={{ position: 'relative', width: '100%', height: 'calc(100vh - 140px)', background: '#0a0e1a', borderRadius: 12, overflow: 'hidden' }}>
            <style>{`
                @keyframes arcDashFlow { to { stroke-dashoffset: -40; } }
                @keyframes arcPulse { 0%,100% { opacity: 0.25; } 50% { opacity: 0.55; } }
                @keyframes bubblePulse { 0%,100% { r: 19; opacity: 0.22; } 50% { r: 24; opacity: 0.55; } }
                .arc-bundled { stroke-dasharray: 6 4; animation: arcDashFlow 2s linear infinite, arcPulse 3s ease-in-out infinite; }
                .arc-focused { stroke-dasharray: 6 4; animation: arcDashFlow 1.2s linear infinite; }
                .bubble-hot { animation: bubblePulse 2.2s ease-in-out infinite; transform-origin: center; }
            `}</style>
            {/* Search bar */}
            <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 10, width: 480 }}>
                <div style={{ background: 'rgba(15, 23, 42, 0.9)', backdropFilter: 'blur(12px)', border: '1px solid rgba(148, 163, 184, 0.2)', borderRadius: 12, padding: 4 }}>
                    <input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search company, person, or deal..."
                        style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#f1f5f9', padding: '12px 16px', fontSize: 15 }}
                    />
                </div>
                {suggestions.length > 0 && (
                    <div style={{ marginTop: 6, background: 'rgba(15, 23, 42, 0.95)', backdropFilter: 'blur(12px)', border: '1px solid rgba(148, 163, 184, 0.2)', borderRadius: 12, overflow: 'hidden', maxHeight: 360, overflowY: 'auto' }}>
                        {suggestions.map((s, i) => (
                            <div key={i} onClick={() => pickSuggestion(s)} style={{ padding: '10px 16px', cursor: 'pointer', color: '#e2e8f0', fontSize: 14, display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid rgba(148,163,184,0.08)' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.15)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: NODE_COLORS[s.nodeType] }}></span>
                                <span style={{ flex: 1 }}>{s.name}</span>
                                <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>{s.nodeType}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Back button */}
            {focusCountry && (
                <button onClick={backToWorld} style={{ position: 'absolute', top: 20, left: 20, zIndex: 10, background: 'rgba(15,23,42,0.9)', backdropFilter: 'blur(12px)', border: '1px solid rgba(148,163,184,0.3)', color: '#f1f5f9', padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    ← Back to World
                </button>
            )}

            {/* Cinematic toggle */}
            <button onClick={() => setCinematic(c => !c)} title="Toggle demo mode"
                style={{ position: 'absolute', top: 20, right: 20, zIndex: 11, background: cinematic ? 'linear-gradient(135deg,#f5c451,#b8862b)' : 'rgba(15,23,42,0.9)', backdropFilter: 'blur(12px)', border: `1px solid ${cinematic ? '#f5c451' : 'rgba(148,163,184,0.3)'}`, color: cinematic ? '#0b1220' : '#f1f5f9', padding: '8px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
                🎬 {cinematic ? 'CINEMATIC ON' : 'CINEMATIC OFF'}
            </button>

            {/* Stats strip + Leaderboard */}
            {!focusCountry && !loading && (
                <>
                    <div style={{ position: 'absolute', bottom: 20, right: 20, zIndex: 10, display: 'flex', gap: 10 }}>
                        <StatChip label="Countries" value={countryData.length} color="#3b82f6" />
                        <StatChip label="Accounts" value={accounts.length} color="#0d9488" />
                        <StatChip label="Deals" value={deals.length} color="#10b981" />
                    </div>
                    <div style={{ position: 'absolute', bottom: 20, left: 20, zIndex: 10, background: 'rgba(15,23,42,0.92)', backdropFilter: 'blur(12px)', border: '1px solid rgba(148,163,184,0.18)', borderRadius: 12, padding: '14px 16px', minWidth: 260, boxShadow: '0 10px 30px rgba(0,0,0,0.4)' }}>
                        <div style={{ color: '#94a3b8', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.4, fontWeight: 700, marginBottom: 10 }}>Top Regions by Value</div>
                        {[...countryData]
                            .map(cd => ({ ...cd, totalVal: cd.deals.reduce((s, d) => s + (Number(d.Value || d.value) || 0), 0) }))
                            .sort((a, b) => b.totalVal - a.totalVal)
                            .slice(0, 5)
                            .map((cd, i) => (
                                <div key={cd.country} onClick={() => zoomToCountry(cd.country)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer', borderRadius: 4 }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.1)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <span style={{ color: '#64748b', fontSize: 11, fontWeight: 700, width: 16 }}>#{i + 1}</span>
                                    <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600, flex: 1 }}>{cd.country}</span>
                                    <span style={{ color: cd.totalVal >= 1000000 ? '#f5c451' : '#94a3b8', fontSize: 12, fontWeight: 700 }}>
                                        {cd.totalVal >= 1000000 ? `$${(cd.totalVal / 1000000).toFixed(1)}M` : cd.totalVal >= 1000 ? `$${(cd.totalVal / 1000).toFixed(0)}K` : cd.totalVal ? `$${cd.totalVal}` : '—'}
                                    </span>
                                </div>
                            ))}
                        {countryData.length === 0 && <div style={{ color: '#64748b', fontSize: 12 }}>No data yet.</div>}
                    </div>
                </>
            )}

            {/* Country header */}
            {focusCountry && focusCountryData && (
                <div style={{ position: 'absolute', top: 80, left: 20, zIndex: 10, background: 'rgba(15,23,42,0.9)', backdropFilter: 'blur(12px)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 12, padding: '14px 18px', color: '#f1f5f9', minWidth: 220 }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{focusCountry}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                        {focusCountryData.accounts.length} accounts · {focusCountryData.deals.length} deals · {focusCountryData.contacts.length} contacts
                    </div>
                </div>
            )}

            {/* Map */}
            <ComposableMap projection="geoMercator" projectionConfig={{ scale: 140 }} style={{ width: '100%', height: '100%' }}>
                <defs>
                    <radialGradient id="glow">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.9" />
                        <stop offset="60%" stopColor="#3b82f6" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                    </radialGradient>
                    <radialGradient id="glowHot">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="1" />
                        <stop offset="60%" stopColor="#10b981" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </radialGradient>
                </defs>
                <ZoomableGroup center={center} zoom={zoom} onMoveEnd={({ coordinates, zoom }) => { setCenter(coordinates); setZoom(zoom); }}>
                    <g style={{ opacity: focusCompany ? 0.35 : 1, transition: 'opacity 400ms' }}>
                        <Geographies geography={GEO_URL}>
                            {({ geographies }) =>
                                geographies.map(geo => (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill="#131a2a"
                                        stroke="#2a3550"
                                        strokeWidth={0.35}
                                        style={{
                                            default: { outline: 'none' },
                                            hover: { fill: '#1c2540', outline: 'none' },
                                            pressed: { outline: 'none' },
                                        }}
                                    />
                                ))
                            }
                        </Geographies>
                    </g>
                    {/* Bundled global arcs — always-on when cinematic, hidden in utility mode */}
                    {cinematic && !focusCompany && globalArcs.map((arc, i) => {
                        const isHover = hoverArc === i;
                        return (
                            <g key={`g-${i}`} onMouseEnter={() => setHoverArc(i)} onMouseLeave={() => setHoverArc(null)} style={{ cursor: 'pointer' }}>
                                <Line
                                    from={arc.fromCoords}
                                    to={arc.toCoords}
                                    stroke={isHover ? '#f5c451' : '#f5c451'}
                                    strokeWidth={isHover ? 2.2 : 0.9 + Math.min(1.5, arc.rootCount * 0.4)}
                                    strokeOpacity={isHover ? 0.95 : 0.35}
                                    strokeLinecap="round"
                                    fill="none"
                                    className={isHover ? 'arc-focused' : 'arc-bundled'}
                                />
                            </g>
                        );
                    })}
                    {/* Focus arcs — multinational siblings */}
                    {focusArcs.map((arc, i) => {
                        const isGold = arc.maxVal >= 1000000;
                        const color = isGold ? '#f5c451' : '#60a5fa';
                        const width = isGold ? 2 : 1.4;
                        return (
                            <Line
                                key={`arc-${i}`}
                                from={arc.from}
                                to={arc.to}
                                stroke={color}
                                strokeWidth={width}
                                strokeOpacity={0.85}
                                strokeLinecap="round"
                                strokeDasharray={isGold ? '' : '4 3'}
                                fill="none"
                            />
                        );
                    })}
                    {/* +N badges on arc destinations */}
                    {focusArcs.filter(a => a.count > 1).map((arc, i) => (
                        <Marker key={`badge-${i}`} coordinates={arc.to}>
                            <g transform="translate(14, -14)">
                                <circle r={8} fill="#0b1220" stroke="#f5c451" strokeWidth={1.2} />
                                <text textAnchor="middle" dy={3} style={{ fontFamily: 'system-ui', fontSize: 9, fill: '#f5c451', fontWeight: 700, pointerEvents: 'none' }}>
                                    +{arc.count}
                                </text>
                            </g>
                        </Marker>
                    ))}
                    {/* Internal team (pink person icons) + External contacts (green person icons) — both anchored by country, jittered */}
                    {(() => {
                        const nodes = [];
                        const PersonIcon = ({ color, selected, dim }) => (
                            <g opacity={dim ? 0.18 : 1}>
                                {selected && <circle r={11} fill={color} opacity={0.3} className="bubble-hot" />}
                                <circle cx={0} cy={-2.5} r={selected ? 2.2 : 1.8} fill={color} stroke="#0b1220" strokeWidth={0.6} />
                                <path d={`M ${selected ? -3.5 : -3} ${selected ? 4 : 3.5} Q 0 ${selected ? 0.5 : 0.5}, ${selected ? 3.5 : 3} ${selected ? 4 : 3.5} L ${selected ? 3.5 : 3} ${selected ? 5.5 : 5} L ${selected ? -3.5 : -3} ${selected ? 5.5 : 5} Z`} fill={color} stroke="#0b1220" strokeWidth={0.5} />
                            </g>
                        );
                        // Internal
                        const byCountryInt = {};
                        internal.forEach(p => {
                            const c = territoryToCountry(p.Territory || p.territory);
                            if (!byCountryInt[c]) byCountryInt[c] = [];
                            byCountryInt[c].push(p);
                        });
                        Object.entries(byCountryInt).forEach(([country, people]) => {
                            const base = COUNTRY_COORDS[country];
                            if (!base) return;
                            people.forEach((p, i) => {
                                const pos = jitter(base, i, people.length);
                                const isSelected = selectedNode?.nodeType === 'internal' && (selectedNode?.Id === (p.Id || p.id) || selectedNode?.name === (p.Name || p.name));
                                const dim = selectedNode && !isSelected;
                                nodes.push(
                                    <Marker key={`int-${p.Id || p.name || i}`} coordinates={pos}
                                        onClick={() => { setSelectedNode({ nodeType: 'internal', Id: p.Id || p.id, name: p.Name || p.name, Email: p.Email, Territory: p.Territory }); setFocusCompany(p.Name || p.name); }}
                                        style={{ default: { cursor: 'pointer' } }}>
                                        <PersonIcon color="#ec4899" selected={isSelected} dim={dim} />
                                    </Marker>
                                );
                            });
                        });
                        // External contacts — country via their company's GTM account
                        const byCountryExt = {};
                        externals.forEach(e => {
                            const company = (e.Company || e.company || '').trim().toLowerCase();
                            const match = accounts.find(a => (a.Name || a.name || '').toLowerCase() === company);
                            const c = match ? normCountry(match.Country || match.country) : null;
                            if (!c) return;
                            if (!byCountryExt[c]) byCountryExt[c] = [];
                            byCountryExt[c].push(e);
                        });
                        Object.entries(byCountryExt).forEach(([country, people]) => {
                            const base = COUNTRY_COORDS[country];
                            if (!base) return;
                            const total = people.length;
                            people.slice(0, 20).forEach((e, i) => {
                                const pos = jitter(base, i + 5, total + 5); // offset ring so not on top of internal
                                const isSelected = selectedNode?.nodeType === 'external' && (selectedNode?.Id === (e.Id || e.id) || selectedNode?.name === (e.Name || e.name));
                                const dim = selectedNode && !isSelected;
                                nodes.push(
                                    <Marker key={`ext-${e.Id || e.name || i}`} coordinates={pos}
                                        onClick={() => { setSelectedNode({ nodeType: 'external', Id: e.Id || e.id, name: e.Name || e.name, Company: e.Company, Position: e.Position }); setFocusCompany(e.Name || e.name); }}
                                        style={{ default: { cursor: 'pointer' } }}>
                                        <PersonIcon color="#10b981" selected={isSelected} dim={dim} />
                                    </Marker>
                                );
                            });
                        });
                        return nodes;
                    })()}
                    {countryData.map(cd => {
                        const coords = COUNTRY_COORDS[cd.country];
                        if (!coords) return null;
                        const topDeal = cd.deals.reduce((m, d) => Math.max(m, Number(d.Value || d.value) || 0), 0);
                        const isGold = topDeal >= 1000000;
                        // Stage-driven color: Won > Active > Prospect > none
                        const stages = cd.deals.map(d => (d.Stage || d.stage || '').toLowerCase());
                        const hasWon = stages.some(s => s.includes('won') || s.includes('closed'));
                        const hasActive = stages.some(s => s && !s.includes('lost') && !s.includes('prospect'));
                        const hasProspect = cd.accounts.some(a => (a.Status || '').toLowerCase().includes('prospect')) || stages.some(s => s.includes('prospect'));
                        const fill = isGold ? '#f5c451' : hasWon ? '#f5c451' : hasActive ? '#10b981' : hasProspect ? '#3b82f6' : '#94a3b8';
                        const ring = isGold ? 'rgba(245,196,81,0.28)' : hasWon ? 'rgba(245,196,81,0.22)' : hasActive ? 'rgba(16,185,129,0.22)' : hasProspect ? 'rgba(59,130,246,0.2)' : 'rgba(148,163,184,0.15)';
                        const isHover = hoverCountry === cd.country;
                        const isFocus = focusCountry === cd.country;
                        const bubbleR = 4;
                        return (
                            <Marker key={cd.country} coordinates={coords}
                                onMouseEnter={() => setHoverCountry(cd.country)}
                                onMouseLeave={() => setHoverCountry(null)}
                                onClick={() => zoomToCountry(cd.country)}
                                style={{ default: { cursor: 'pointer' } }}>
                                {/* soft halo — pulsing for gold */}
                                <circle r={bubbleR + 8} fill={ring} className={isGold || hasWon ? 'bubble-hot' : ''} />
                                {/* tiny sharp dot */}
                                <circle r={bubbleR} fill={fill} stroke="#0b1220" strokeWidth={1} />
                                {/* label on hover/focus */}
                                {(isHover || isFocus) && (
                                    <g pointerEvents="none">
                                        <rect x={-48} y={-bubbleR - 22} width={96} height={18} rx={4} fill="rgba(11,18,32,0.95)" stroke={fill} strokeWidth={0.6} />
                                        <text textAnchor="middle" y={-bubbleR - 9} style={{ fontFamily: 'system-ui', fontSize: 10, fill: '#e2e8f0', fontWeight: 600 }}>
                                            {cd.country} · {cd.accounts.length}
                                        </text>
                                    </g>
                                )}
                            </Marker>
                        );
                    })}
                </ZoomableGroup>
            </ComposableMap>

            {/* Country drill-in overlay: company list + ego graph */}
            <AnimatePresence>
                {(focusCountry || selectedNode) && (
                    <motion.div initial={{ opacity: 0, x: 400 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 400 }}
                        style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 380, background: '#111827', borderLeft: '1px solid rgba(148,163,184,0.15)', padding: 20, overflowY: 'auto', zIndex: 9, boxShadow: '-20px 0 40px rgba(0,0,0,0.4)' }}>
                        <button onClick={() => { setSelectedNode(null); setFocusCompany(null); setConnections([]); if (!focusCountry) {} }} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12, marginBottom: 12, padding: 0 }}>✕ Close</button>
                        {selectedNode && !focusCountryData && (
                            <div style={{ marginBottom: 18, padding: 14, background: '#1a2236', borderRadius: 8, border: '1px solid rgba(148,163,184,0.1)' }}>
                                <div style={{ color: '#94a3b8', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{selectedNode.nodeType}</div>
                                <div style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 700 }}>{selectedNode.name}</div>
                                {selectedNode.Email && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{selectedNode.Email}</div>}
                                {selectedNode.Company && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{selectedNode.Company}</div>}
                                {selectedNode.Status && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>Status: {selectedNode.Status}</div>}
                                {selectedNode.TaskOwner && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>Owner: {selectedNode.TaskOwner}</div>}
                            </div>
                        )}
                        {focusCountryData && <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14, fontWeight: 600 }}>Accounts in {focusCountry}</div>}
                        {focusCountryData && focusCountryData.accounts.slice(0, 40).map(a => {
                            const name = a.Name || a.name;
                            const initial = (name || '?').charAt(0).toUpperCase();
                            const countryDeals = focusCountryData.deals.filter(d => (d.Company || '').toLowerCase() === (name || '').toLowerCase());
                            const maxVal = countryDeals.reduce((m, d) => Math.max(m, Number(d.Value || d.value) || 0), 0);
                            const isGold = maxVal >= 1000000;
                            const isFocus = focusCompany === name;
                            return (
                                <div key={a.Id || a.id} onClick={() => { setFocusCompany(name); zoomToCountry(focusCountry); loadConnections('gtm', a.Id || a.id); }}
                                    style={{ padding: 12, marginBottom: 6, background: isFocus ? 'rgba(245,196,81,0.08)' : '#1a2236', border: `1px solid ${isFocus ? '#f5c451' : 'rgba(148,163,184,0.08)'}`, borderRadius: 8, cursor: 'pointer', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 12, transition: 'all 150ms' }}
                                    onMouseEnter={e => { if (!isFocus) e.currentTarget.style.background = '#202a42'; }}
                                    onMouseLeave={e => { if (!isFocus) e.currentTarget.style.background = '#1a2236'; }}>
                                    <div style={{ width: 34, height: 34, borderRadius: 6, background: isGold ? 'linear-gradient(135deg,#f5c451,#b8862b)' : 'linear-gradient(135deg,#3b4f7a,#1f2a44)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{initial}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                            {a.Sector || a.sector || '—'}{countryDeals.length > 0 ? ` · ${countryDeals.length} deal${countryDeals.length > 1 ? 's' : ''}` : ''}
                                        </div>
                                    </div>
                                    {isGold && <span style={{ fontSize: 10, color: '#f5c451', fontWeight: 700, letterSpacing: 0.5 }}>GOLD</span>}
                                </div>
                            );
                        })}
                        {focusCountryData && focusCountryData.accounts.length === 0 && (
                            <div style={{ color: '#64748b', fontSize: 13, padding: 12 }}>No accounts yet.</div>
                        )}

                        {/* Connections panel */}
                        {connections.length > 0 && (
                            <div style={{ marginTop: 24 }}>
                                <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Connections for {focusCompany}</div>
                                {connections.map((c, i) => (
                                    <div key={i} style={{ padding: 10, marginBottom: 6, background: 'rgba(30,41,59,0.4)', borderLeft: `3px solid ${NODE_COLORS[c.nodeType]}`, borderRadius: 4, color: '#e2e8f0' }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{c.connectionLabel} · {c.nodeType}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Country Constellation Overlay */}
            <AnimatePresence>
                {focusCountry && focusCountryData && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(15,23,42,0.7) 0%, rgba(5,8,15,0.95) 80%)', backdropFilter: 'blur(2px)', zIndex: 5, pointerEvents: 'none' }}>
                        <svg viewBox="-500 -350 1000 700" preserveAspectRatio="xMidYMid meet" style={{ width: '100%', height: '100%', pointerEvents: 'auto' }}>
                            <defs>
                                <radialGradient id="floorGlow">
                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25" />
                                    <stop offset="60%" stopColor="#3b82f6" stopOpacity="0.08" />
                                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                                </radialGradient>
                            </defs>
                            {/* Floor glow */}
                            <ellipse cx={0} cy={50} rx={440} ry={230} fill="url(#floorGlow)" />
                            {/* Country label */}
                            <text textAnchor="middle" y={-250} style={{ fontFamily: 'system-ui', fontSize: 42, fill: '#f1f5f9', fontWeight: 800, letterSpacing: 2 }}>
                                {focusCountry.toUpperCase()}
                            </text>
                            <text textAnchor="middle" y={-220} style={{ fontFamily: 'system-ui', fontSize: 13, fill: '#94a3b8', letterSpacing: 4, fontWeight: 600 }}>
                                {focusCountryData.accounts.length} ACCOUNTS · {focusCountryData.deals.length} DEALS · {focusCountryData.contacts.length} CONTACTS
                            </text>
                            {(() => {
                                // Gather nodes: accounts + internal(by territory) + externals(by company)
                                const items = [];
                                focusCountryData.accounts.slice(0, 30).forEach(a => items.push({ type: 'account', id: `a-${a.Id}`, name: a.Name || a.name, raw: a }));
                                internal.filter(p => territoryToCountry(p.Territory || p.territory) === focusCountry).slice(0, 20).forEach(p => items.push({ type: 'internal', id: `i-${p.Id}`, name: p.Name || p.name, raw: p }));
                                focusCountryData.contacts.slice(0, 20).forEach(e => items.push({ type: 'external', id: `e-${e.Id}`, name: e.Name || e.name, raw: e }));

                                // Scatter deterministically: spiral layout
                                const positions = {};
                                items.forEach((n, i) => {
                                    const angle = i * 0.55;
                                    const r = 60 + i * 11;
                                    positions[n.id] = [Math.cos(angle) * r, Math.sin(angle) * r * 0.55 + 40];
                                });
                                // Edges: internal→account (same company), external→account(same company)
                                const edges = [];
                                items.filter(n => n.type === 'external').forEach(ext => {
                                    const accMatch = items.find(n => n.type === 'account' && (n.name || '').toLowerCase() === (ext.raw.Company || '').toLowerCase());
                                    if (accMatch) edges.push([ext.id, accMatch.id, '#10b981']);
                                });
                                items.filter(n => n.type === 'internal').forEach(int => {
                                    items.filter(n => n.type === 'account').slice(0, 2).forEach(acc => edges.push([int.id, acc.id, 'rgba(236,72,153,0.25)']));
                                });

                                // Overseas arrows: fuzzy siblings of accounts in other countries
                                const overseas = [];
                                items.filter(n => n.type === 'account').forEach((acc, idx) => {
                                    const root = rootName(acc.name);
                                    if (!root || root.length < 3) return;
                                    const siblings = accounts.filter(a2 => {
                                        const c2 = normCountry(a2.Country || a2.country);
                                        return c2 && c2 !== focusCountry && rootName(a2.Name || a2.name) === root;
                                    });
                                    if (siblings.length > 0) {
                                        const destCountry = normCountry(siblings[0].Country || siblings[0].country);
                                        overseas.push({ from: positions[acc.id], label: destCountry, name: root.charAt(0).toUpperCase() + root.slice(1), count: siblings.length });
                                    }
                                });

                                return (
                                    <>
                                        {/* Edges */}
                                        {edges.map(([a, b, color], i) => {
                                            const p1 = positions[a], p2 = positions[b];
                                            if (!p1 || !p2) return null;
                                            return <line key={`e-${i}`} x1={p1[0]} y1={p1[1]} x2={p2[0]} y2={p2[1]} stroke={color} strokeWidth={1} opacity={0.6} />;
                                        })}
                                        {/* Overseas arrows */}
                                        {overseas.slice(0, 8).map((o, i) => {
                                            const angle = Math.atan2(o.from[1], o.from[0]);
                                            const ox = Math.cos(angle) * 460;
                                            const oy = Math.sin(angle) * 280;
                                            return (
                                                <g key={`os-${i}`} opacity={0.85}>
                                                    <line x1={o.from[0]} y1={o.from[1]} x2={ox} y2={oy} stroke="#f5c451" strokeWidth={1.2} strokeDasharray="4 3" className="arc-focused" />
                                                    <text x={ox} y={oy} textAnchor={ox > 0 ? 'start' : 'end'} dy={oy > 0 ? 14 : -4} style={{ fontFamily: 'system-ui', fontSize: 10, fill: '#f5c451', fontWeight: 700 }}>
                                                        → {o.label} · {o.name}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                        {/* Nodes */}
                                        {items.map(n => {
                                            const pos = positions[n.id];
                                            if (!pos) return null;
                                            const color = n.type === 'account' ? '#3b82f6' : n.type === 'internal' ? '#ec4899' : '#10b981';
                                            return (
                                                <g key={n.id} transform={`translate(${pos[0]}, ${pos[1]})`} style={{ cursor: 'pointer' }}
                                                    onClick={() => { setSelectedNode({ nodeType: n.type === 'account' ? 'gtm' : n.type, Id: n.raw.Id, name: n.name, ...n.raw }); setFocusCompany(n.name); loadConnections(n.type === 'account' ? 'gtm' : n.type, n.raw.Id); }}>
                                                    {n.type === 'account' ? (
                                                        <>
                                                            <circle r={7} fill={color} stroke="#0b1220" strokeWidth={1.2} />
                                                            <circle r={11} fill={color} opacity={0.18} />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <circle cx={0} cy={-3} r={2.4} fill={color} stroke="#0b1220" strokeWidth={0.6} />
                                                            <path d={`M -3.5 4 Q 0 0.5, 3.5 4 L 3.5 5.5 L -3.5 5.5 Z`} fill={color} stroke="#0b1220" strokeWidth={0.5} />
                                                        </>
                                                    )}
                                                    <text textAnchor="middle" y={18} style={{ fontFamily: 'system-ui', fontSize: 9, fill: '#cbd5e1', fontWeight: 500, pointerEvents: 'none' }}>
                                                        {(n.name || '').length > 18 ? (n.name || '').slice(0, 17) + '…' : n.name}
                                                    </text>
                                                </g>
                                            );
                                        })}
                                    </>
                                );
                            })()}
                        </svg>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Arc tooltip */}
            {hoverArc !== null && globalArcs[hoverArc] && (
                <div style={{ position: 'absolute', top: 70, left: '50%', transform: 'translateX(-50%)', zIndex: 11, background: 'rgba(11,18,32,0.95)', backdropFilter: 'blur(10px)', border: '1px solid #f5c451', borderRadius: 10, padding: '10px 14px', color: '#f1f5f9', fontSize: 12, pointerEvents: 'none', boxShadow: '0 10px 30px rgba(245,196,81,0.2)' }}>
                    <div style={{ color: '#f5c451', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700, marginBottom: 3 }}>
                        {globalArcs[hoverArc].from} ↔ {globalArcs[hoverArc].to}
                    </div>
                    <div style={{ fontWeight: 600 }}>
                        {globalArcs[hoverArc].rootLabel} Network — {globalArcs[hoverArc].rootCount} {globalArcs[hoverArc].rootCount === 1 ? 'company' : 'companies'}
                    </div>
                </div>
            )}

            {loading && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 14 }}>
                    Loading global view…
                </div>
            )}
        </div>
    );
}

function StatChip({ label, value, color }) {
    return (
        <div style={{ background: 'rgba(15,23,42,0.9)', backdropFilter: 'blur(12px)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }}></span>
            <span style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase' }}>{label}</span>
            <span style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 700 }}>{value}</span>
        </div>
    );
}
